import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Why Us", href: "#why-us" },
  { label: "Industries", href: "#industries" },
  { label: "Process", href: "#process" },
  { label: "Insights", href: "#insights" },
  { label: "Contact", href: "#contact" },
];

const LOGO_URL = "/zevnox-logo-white.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B0B0B]/75 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <a
          href="#home"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 group rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
          aria-label="Zevnox Search — Home"
        >
          <img
            src="/zevnox-logo-white.png"
            alt="Zevnox Search"
            data-testid="brand-logo"
            width="180"
            height="56"
            fetchpriority="high"
            decoding="async"
            className="h-12 sm:h-14 w-auto object-contain select-none transition-all duration-500 group-hover:scale-[1.02]"
            style={{
              imageRendering: "crisp-edges",
              filter:
                "drop-shadow(0 0 6px rgba(255,255,255,0.25)) drop-shadow(0 0 18px rgba(217,249,157,0.25))",
            }}
            draggable={false}
          />
        </a>

        <nav
          aria-label="Primary"
          className="hidden lg:flex items-center gap-9"
          data-testid="primary-nav"
        >
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative text-[13px] uppercase tracking-[0.18em] font-medium text-neutral-300 hover:text-white transition-colors duration-300 group rounded focus-visible:outline-none focus-visible:text-white"
            >
              {item.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#D9F99D] transition-all duration-300 group-hover:w-full group-focus-visible:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="#contact"
            data-testid="header-cta-hire"
            className="inline-flex items-center gap-2 rounded-full bg-[#D9F99D] hover:bg-[#E4FBB1] text-black text-sm font-semibold px-5 py-2.5 transition-all zx-cta-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
          >
            Hire Talent
            <span className="block h-1.5 w-1.5 rounded-full bg-[#0B0B0B]/80 zx-pulse" aria-hidden="true" />
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-white/80 hover:text-white hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D]/60"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav-panel"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-nav-panel"
          className="lg:hidden border-t border-white/5 bg-[#0B0B0B]/95 backdrop-blur-xl"
          data-testid="mobile-nav-panel"
        >
          <div className="px-6 py-7 flex flex-col gap-1">
            {NAV.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between py-3 text-neutral-200 hover:text-white text-[15px] border-b border-white/5 last:border-none"
              >
                <span>{item.label}</span>
                <span className="text-neutral-600 text-xs font-mono">→</span>
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#D9F99D] text-black text-sm font-semibold px-5 py-3.5 zx-cta-glow"
            >
              Hire Talent
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
